// Ventholes icon set. One family, 16px grid, 1.4px stroke, round caps.
// Drawn in-house; keep every icon consistent with these rules.

import type { ReactNode } from 'react'

function Icon({ children, size = 16, filled = false }: { children: ReactNode; size?: number; filled?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

type P = { size?: number }

/* Notum Robotics mark (n-r.hr), geometry verbatim from their logo.svg,
   recolored via currentColor so it follows the theme. */
export const NotumMark = ({ size = 16 }: { size?: number }) => (
  <svg
    width={(size * 122) / 101}
    height={size}
    viewBox="0 0 122 101"
    fill="currentColor"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <g transform="matrix(1.12439,0,0,1.12439,-517.791,-270.172)">
      <path
        fillRule="nonzero"
        d="M559.829,314.861L558.57,314.861L558.558,314.088L559.636,314.088C559.816,314.088 559.982,314.061 560.134,314.009C560.287,313.956 560.41,313.879 560.503,313.777C560.597,313.676 560.644,313.551 560.644,313.402C560.644,313.207 560.612,313.055 560.547,312.945C560.483,312.836 560.374,312.756 560.222,312.705C560.07,312.654 559.865,312.629 559.607,312.629L558.833,312.629L558.833,316.842L557.972,316.842L557.972,311.861L559.607,311.861C559.994,311.861 560.329,311.919 560.615,312.034C560.9,312.149 561.12,312.32 561.274,312.547C561.428,312.773 561.505,313.055 561.505,313.39C561.505,313.601 561.456,313.789 561.359,313.953C561.261,314.117 561.118,314.258 560.928,314.375C560.739,314.492 560.505,314.588 560.228,314.662C560.216,314.662 560.204,314.675 560.19,314.7C560.176,314.725 560.163,314.738 560.152,314.738C560.085,314.777 560.043,314.806 560.026,314.823C560.008,314.841 559.991,314.851 559.973,314.855C559.955,314.859 559.908,314.861 559.829,314.861ZM559.771,314.861L559.894,314.234C560.503,314.234 560.92,314.371 561.145,314.644C561.37,314.918 561.482,315.262 561.482,315.676L561.482,316.004C561.482,316.148 561.49,316.286 561.505,316.417C561.521,316.548 561.546,316.658 561.581,316.748L561.581,316.842L560.697,316.842C560.658,316.74 560.635,316.596 560.629,316.408C560.623,316.221 560.62,316.084 560.62,315.998L560.62,315.681C560.62,315.4 560.556,315.193 560.427,315.06C560.298,314.928 560.079,314.861 559.771,314.861ZM556.226,314.422C556.226,314.937 556.316,315.418 556.495,315.863C556.675,316.308 556.923,316.698 557.24,317.032C557.556,317.366 557.923,317.626 558.341,317.811C558.759,317.997 559.206,318.09 559.683,318.09C560.163,318.09 560.612,317.997 561.028,317.811C561.444,317.626 561.81,317.366 562.126,317.032C562.443,316.698 562.691,316.308 562.87,315.863C563.05,315.418 563.14,314.937 563.14,314.422C563.14,313.902 563.05,313.421 562.87,312.977C562.691,312.534 562.443,312.146 562.126,311.814C561.81,311.482 561.444,311.224 561.028,311.041C560.612,310.857 560.165,310.765 559.689,310.765C559.212,310.765 558.764,310.857 558.344,311.041C557.924,311.224 557.556,311.482 557.24,311.814C556.923,312.146 556.675,312.534 556.495,312.977C556.316,313.421 556.226,313.902 556.226,314.422ZM555.529,314.422C555.529,313.801 555.636,313.224 555.851,312.693C556.066,312.162 556.365,311.698 556.747,311.302C557.13,310.905 557.573,310.597 558.075,310.376C558.577,310.155 559.115,310.045 559.689,310.045C560.263,310.045 560.801,310.155 561.303,310.376C561.805,310.597 562.246,310.905 562.627,311.302C563.008,311.698 563.306,312.162 563.521,312.693C563.736,313.224 563.843,313.801 563.843,314.422C563.843,315.043 563.736,315.619 563.521,316.15C563.306,316.681 563.008,317.147 562.627,317.548C562.246,317.948 561.805,318.259 561.303,318.479C560.801,318.7 560.263,318.81 559.689,318.81C559.115,318.81 558.577,318.699 558.075,318.476C557.573,318.254 557.13,317.943 556.747,317.545C556.365,317.146 556.066,316.681 555.851,316.15C555.636,315.619 555.529,315.043 555.529,314.422Z"
      />
    </g>
    <g transform="matrix(2.03084,0,0,1.2566,-481.949,-350.97)">
      <g transform="matrix(0.679183,0,0,1.09766,216.804,-103.561)">
        <path d="M36.7,400.1L30.2,396.4L30.2,373.9L36.7,370.2L36.7,400.1ZM51.9,372.4L51.9,408.5L42.5,403.1L42.5,367L74.1,348.8L89,357.4L89,391.7L88.1,392.2L79.5,397.1L79.5,362.9L74.1,359.8L51.9,372.4Z" />
      </g>
      <g transform="matrix(-0.679183,-1.34424e-16,8.31759e-17,-1.09766,317.47,741.792)">
        <path d="M36.7,400.1L30.2,396.4L30.2,373.9L36.7,370.2L36.7,400.1ZM51.9,372.4L51.9,408.5L42.5,403.1L42.5,367L74.1,348.8L89,357.4L89,391.7L88.1,392.2L79.5,397.1L79.5,362.9L74.1,359.8L51.9,372.4Z" />
      </g>
    </g>
  </svg>
)

/* Brand mark: panel with three holes */
export const IconBrand = ({ size }: P) => (
  <Icon size={size}>
    <rect x="1.7" y="2.7" width="12.6" height="10.6" />
    <circle cx="5.6" cy="6.5" r="1.5" />
    <circle cx="10.4" cy="6.5" r="1.1" />
    <circle cx="5.6" cy="10.2" r="1.1" />
  </Icon>
)

/* Layout modes */
export const IconGrid = ({ size }: P) => (
  <Icon size={size} filled>
    {[3, 8, 13].map((x) => [3, 8, 13].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.35" />))}
  </Icon>
)

export const IconScatter = ({ size }: P) => (
  <Icon size={size} filled>
    <circle cx="4" cy="4.4" r="1.6" />
    <circle cx="11.6" cy="3.6" r="1.1" />
    <circle cx="13" cy="9" r="1.5" />
    <circle cx="7.6" cy="8.4" r="1.2" />
    <circle cx="3.4" cy="12" r="1.2" />
    <circle cx="9.6" cy="13" r="1.5" />
  </Icon>
)

export const IconSpiral = ({ size }: P) => (
  <Icon size={size}>
    <path d="M8 8 m0 -0.8 a0.8 0.8 0 0 1 0.8 0.8 a1.9 1.9 0 0 1 -3.2 1.4 a3.2 3.2 0 0 1 1 -5.2 a4.9 4.9 0 0 1 6 2.4 a6 6 0 0 1 -2.4 7.6" />
  </Icon>
)

export const IconRings = ({ size }: P) => (
  <Icon size={size}>
    <circle cx="8" cy="8" r="1.6" />
    <circle cx="8" cy="8" r="4" />
    <circle cx="8" cy="8" r="6.4" />
  </Icon>
)

export const IconPhyllo = ({ size }: P) => (
  <Icon size={size} filled>
    <circle cx="8" cy="8" r="1" />
    <circle cx="10.8" cy="7" r="1.05" />
    <circle cx="8.6" cy="11" r="1.1" />
    <circle cx="5" cy="9.6" r="1.15" />
    <circle cx="5.4" cy="5" r="1.2" />
    <circle cx="9.8" cy="3.4" r="1.25" />
    <circle cx="13" cy="10.6" r="1.3" />
    <circle cx="4.6" cy="13.2" r="1.15" />
    <circle cx="2.6" cy="6.4" r="1" />
  </Icon>
)

export const IconHatch = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.5 6 L10 13.5 M6 2.5 L13.5 10 M13.5 6 L6 13.5 M10 2.5 L2.5 10" />
  </Icon>
)

export const IconFlow = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2 4.5 C6 2.5 10 6.5 14 4.5 M2 8 C6 6 10 10 14 8 M2 11.5 C6 9.5 10 13.5 14 11.5" />
  </Icon>
)

export const IconPath = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.5 12 C6 12 5.5 4 13.5 4" strokeDasharray="2.6 2" />
    <circle cx="3.4" cy="11.7" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="7.6" cy="7.2" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="12.6" cy="4.2" r="1.3" fill="currentColor" stroke="none" />
  </Icon>
)

export const IconVoronoi = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2 2 H14 V14 H2 Z M6.5 2 L7.4 6.2 L2 8.6 M7.4 6.2 L12 4.6 L14 5 M7.4 6.2 L8.8 10.4 L14 11.2 M8.8 10.4 L5 14 M8.8 10.4 L2 8.6" />
  </Icon>
)

/* Actions */
export const IconUndo = ({ size }: P) => (
  <Icon size={size}>
    <path d="M3 6.5 H10 A3.5 3.5 0 0 1 10 13.5 H6" />
    <path d="M5.8 3.7 L3 6.5 L5.8 9.3" />
  </Icon>
)

export const IconRedo = ({ size }: P) => (
  <Icon size={size}>
    <path d="M13 6.5 H6 A3.5 3.5 0 0 0 6 13.5 H10" />
    <path d="M10.2 3.7 L13 6.5 L10.2 9.3" />
  </Icon>
)

export const IconHistory = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.6 8 A5.4 5.4 0 1 0 8 2.6 A5.6 5.6 0 0 0 3.6 4.8" />
    <path d="M3.4 2.4 L3.6 4.9 L6.1 4.7" />
    <path d="M8 5.2 V8.2 L10.4 9.6" />
  </Icon>
)

export const IconDice = ({ size }: P) => (
  <Icon size={size}>
    <rect x="2.5" y="2.5" width="11" height="11" />
    <circle cx="5.5" cy="5.5" r="0.95" fill="currentColor" stroke="none" />
    <circle cx="10.5" cy="5.5" r="0.95" fill="currentColor" stroke="none" />
    <circle cx="8" cy="8" r="0.95" fill="currentColor" stroke="none" />
    <circle cx="5.5" cy="10.5" r="0.95" fill="currentColor" stroke="none" />
    <circle cx="10.5" cy="10.5" r="0.95" fill="currentColor" stroke="none" />
  </Icon>
)

export const IconImport = ({ size }: P) => (
  <Icon size={size}>
    <path d="M8 2.5 V9.5 M5.2 7 L8 9.8 L10.8 7" />
    <path d="M2.5 11 V13.5 H13.5 V11" />
  </Icon>
)

export const IconExport = ({ size }: P) => (
  <Icon size={size}>
    <path d="M8 9.5 V2.5 M5.2 5 L8 2.2 L10.8 5" />
    <path d="M2.5 11 V13.5 H13.5 V11" />
  </Icon>
)

export const IconClose = ({ size }: P) => (
  <Icon size={size}>
    <path d="M4 4 L12 12 M12 4 L4 12" />
  </Icon>
)

export const IconChevronDown = ({ size }: P) => (
  <Icon size={size}>
    <path d="M4 6.2 L8 10.2 L12 6.2" />
  </Icon>
)

export const IconPoint = ({ size }: P) => (
  <Icon size={size}>
    <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="8" cy="8" r="5.4" strokeDasharray="2.4 2.1" />
  </Icon>
)

export const IconLine = ({ size }: P) => (
  <Icon size={size}>
    <path d="M4 12 L12 4" />
    <circle cx="4" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="4" r="1.4" fill="currentColor" stroke="none" />
  </Icon>
)

export const IconCurve = ({ size }: P) => (
  <Icon size={size}>
    <path d="M3 12.5 C6.5 12.5 6 4.5 13 3.8" />
    <circle cx="3" cy="12.5" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="13" cy="3.8" r="1.3" fill="currentColor" stroke="none" />
  </Icon>
)

export const IconPolyline = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.6 12.6 L6.4 6 L10 9.6 L13.4 3.4" />
    <circle cx="2.6" cy="12.6" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="6.4" cy="6" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="10" cy="9.6" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="13.4" cy="3.4" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
)

export const IconSun = ({ size }: P) => (
  <Icon size={size}>
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5 V3 M8 13 V14.5 M1.5 8 H3 M13 8 H14.5 M3.4 3.4 L4.5 4.5 M11.5 11.5 L12.6 12.6 M12.6 3.4 L11.5 4.5 M4.5 11.5 L3.4 12.6" />
  </Icon>
)

export const IconMoon = ({ size }: P) => (
  <Icon size={size}>
    <path d="M13 9.6 A5.6 5.6 0 1 1 6.4 3 A4.4 4.4 0 0 0 13 9.6 Z" />
  </Icon>
)

export const IconCommand = ({ size }: P) => (
  <Icon size={size}>
    <path d="M6 6 H10 V10 H6 Z" />
    <path d="M6 6 H4.2 A1.8 1.8 0 1 1 6 4.2 Z M10 6 V4.2 A1.8 1.8 0 1 1 11.8 6 Z M10 10 H11.8 A1.8 1.8 0 1 1 10 11.8 Z M6 10 V11.8 A1.8 1.8 0 1 1 4.2 10 Z" />
  </Icon>
)

export const IconKeyboard = ({ size }: P) => (
  <Icon size={size}>
    <rect x="1.8" y="4" width="12.4" height="8" />
    <path d="M4 6.5 H4.01 M6.5 6.5 H6.51 M9 6.5 H9.01 M11.5 6.5 H11.51 M4.5 9.5 H11.5" />
  </Icon>
)

export const IconCenter = ({ size }: P) => (
  <Icon size={size}>
    <circle cx="8" cy="8" r="4.2" />
    <circle cx="8" cy="8" r="1.1" fill="currentColor" stroke="none" />
    <path d="M8 1.6 V4 M8 12 V14.4 M1.6 8 H4 M12 8 H14.4" />
  </Icon>
)

export const IconRuler = ({ size }: P) => (
  <Icon size={size}>
    <rect x="1.2" y="9.2" width="16" height="5" transform="rotate(-45 8 8)" />
    <path d="M5.2 8.4 L6.4 9.6 M7.4 6.2 L8.6 7.4 M9.6 4 L10.8 5.2" />
  </Icon>
)

export const IconFit = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.5 5.5 V2.5 H5.5 M10.5 2.5 H13.5 V5.5 M13.5 10.5 V13.5 H10.5 M5.5 13.5 H2.5 V10.5" />
    <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
)

export const IconCheck = ({ size }: P) => (
  <Icon size={size}>
    <path d="M3 8.6 L6.5 12 L13 4.5" />
  </Icon>
)

export const IconWarning = ({ size }: P) => (
  <Icon size={size}>
    <path d="M8 2.2 L14.5 13.4 H1.5 Z" />
    <path d="M8 6.2 V9.4 M8 11.4 V11.5" />
  </Icon>
)

export const IconTrash = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.8 4.4 H13.2 M6 4.4 V3 H10 V4.4 M4.2 4.4 L4.9 13.3 H11.1 L11.8 4.4" />
    <path d="M6.7 7 V10.8 M9.3 7 V10.8" />
  </Icon>
)

export const IconReset = ({ size }: P) => (
  <Icon size={size}>
    <path d="M13.4 8 A5.4 5.4 0 1 1 8 2.6 A5.6 5.6 0 0 1 12.4 4.8" />
    <path d="M12.7 2.3 L12.5 4.9 L9.9 4.6" />
  </Icon>
)

/* Export formats */
export const IconVector = ({ size }: P) => (
  <Icon size={size}>
    <path d="M3.5 12.5 C3.5 7 7 3.5 12.5 3.5" />
    <rect x="2" y="11" width="3" height="3" />
    <rect x="11" y="2" width="3" height="3" />
  </Icon>
)

export const IconMesh = ({ size }: P) => (
  <Icon size={size}>
    <path d="M8 1.8 L14 5 V11 L8 14.2 L2 11 V5 Z" />
    <path d="M2 5 L8 8 L14 5 M8 8 V14.2" />
  </Icon>
)

export const IconSolid = ({ size }: P) => (
  <Icon size={size}>
    <path d="M2.2 5.8 L8 3 L13.8 5.8 L8 8.6 Z" />
    <path d="M2.2 5.8 V10.2 L8 13 L13.8 10.2 V5.8 M8 8.6 V13" />
    <circle cx="8" cy="5.8" r="1" />
  </Icon>
)

export const IconGallery = ({ size }: P) => (
  <Icon size={size}>
    <rect x="2" y="2" width="5.2" height="5.2" />
    <rect x="8.8" y="2" width="5.2" height="5.2" />
    <rect x="2" y="8.8" width="5.2" height="5.2" />
    <rect x="8.8" y="8.8" width="5.2" height="5.2" />
  </Icon>
)

export const IconFile = ({ size }: P) => (
  <Icon size={size}>
    <path d="M3.5 1.8 H9.5 L12.5 4.8 V14.2 H3.5 Z" />
    <path d="M9.5 1.8 V4.8 H12.5" />
  </Icon>
)
